import { CommonModule, Location } from '@angular/common'
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    ElementRef,
    Input,
    isDevMode,
    QueryList
} from '@angular/core'
import { Router, RouterModule } from '@angular/router'
import { NavigationHistoryService } from 'src/app/services/navigation-history.service'

import { DetailPageRegistry } from '../detail-page.decorator'
import { DetailSectionComponent } from '../detail-section/detail-section.component'

/**
 * Top-level chrome for any single-entity detail page.
 *
 * Composition contract:
 *   <app-detail-page-shell [back]="..." [loading]="..." [errorMessage]="...">
 *       <app-detail-header header [model]="headerModel" />
 *       <app-detail-section title="KPIs">...</app-detail-section>
 *       <app-detail-section title="Efficiency">...</app-detail-section>
 *   </app-detail-page-shell>
 *
 * The "header" projected slot uses a CSS attribute selector; sections are the
 * default slot. The shell owns: breadcrumb back button, loading state,
 * error card, sticky header chrome, last-refresh chip.
 */
@Component({
    selector: 'app-detail-page-shell',
    standalone: true,
    imports: [CommonModule, RouterModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './detail-page-shell.component.html',
    styleUrls: ['./detail-page-shell.component.scss']
})
export class DetailPageShellComponent implements AfterContentInit {
    @Input() back?: { route: string[] }
    @Input() loading = false
    @Input() errorMessage: string | null = null
    @Input() lastRefreshAt: Date | null = null
    @Input() testId?: string

    @ContentChildren(DetailSectionComponent) sections!: QueryList<DetailSectionComponent>

    constructor(
        private readonly _host: ElementRef<HTMLElement>,
        private readonly _router: Router,
        private readonly _location: Location,
        private readonly _navHistory: NavigationHistoryService,
    ) {}

    ngAfterContentInit(): void {
        if (!isDevMode()) return
        // Walk up to the host detail-page component and verify it carries @DetailPage().
        // This is a best-effort sanity check that complements the CI lint — it warns
        // when a detail page renders the shell without claiming detail-page-ness.
        queueMicrotask(() => this._verifyHostDecorator())
    }

    goBack(): void {
        // Prefer the browser back stack so the user returns to wherever they
        // navigated in from. Use NavigationHistoryService rather than
        // window.history.length — the latter counts pre-app entries (Auth0
        // redirects, the tab's prior URLs) and would walk into a redirect
        // chain that lands the user back on this page.
        if (this._navHistory.canGoBack()) {
            this._location.back()
            return
        }
        if (this.back?.route?.length) {
            this._router.navigate(this.back.route)
        }
    }

    private _verifyHostDecorator(): void {
        const node = this._host.nativeElement
        // Find the nearest enclosing component element by walking parent chain.
        // Angular doesn't expose component instances cleanly without DI tricks,
        // so we look for a parent with `__ngContext__` and read its component
        // through the LContext type. If that fails, we silently skip — this is
        // dev-mode advisory only.
        const ngContextKey = Object.keys(node).find(k => k.startsWith('__ngContext__'))
        if (!ngContextKey) return
        // Walk up DOM to find the page component instance.
        let cur: HTMLElement | null = node.parentElement
        while (cur) {
            const ctxKey = Object.keys(cur).find(k => k.startsWith('__ngContext__'))
            if (ctxKey) {
                const ctx = (cur as unknown as Record<string, unknown[]>)[ctxKey]
                // Heuristic: scan the LView for an instance whose constructor was registered.
                const found = Array.isArray(ctx) && ctx.some(v =>
                    v && typeof v === 'object' && DetailPageRegistry.has(v.constructor)
                )
                if (found) return
            }
            cur = cur.parentElement
        }
        // eslint-disable-next-line no-console
        console.warn(
            '[detail-page-shell] Host component is not annotated with @DetailPage(). ' +
            'Apply the decorator above @Component to satisfy the visual-consistency contract. ' +
            'See frontend/src/app/modules/shared/detail-page/README.md.'
        )
    }
}
